import { useEffect, useMemo, useRef, useState } from 'react';
import { IconBookmark, IconFileText, IconHeading } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { OBJ } from '../constants';
import { readTextLines } from '../engine/pdfEngine';
import { usePdfEditorStore } from '../state/usePdfEditorStore';

/**
 * Outline view: the document's structure instead of its pages.
 *
 * Word builds this from heading styles. A PDF has none, so the structure comes
 * from the three things that do say where a document's sections are: the
 * bookmarks, the largest piece of text this editor has added to a page, and —
 * for every page nobody has touched, which on an ordinary document is all of
 * them — the largest line in the page's own text layer.
 *
 * That last one is why the reading below exists. Without it the outline of a
 * freshly opened PDF is a list of "no heading", because the editor's own
 * objects are the only text it knew about and there are none yet.
 *
 * Clicking a row goes to the page, exactly as it does in Word.
 */
export default function OutlineView() {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const pages = usePdfEditorStore((s) => s.pages);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);
    const bookmarks = usePdfEditorStore((s) => s.bookmarks);
    const currentPage = usePdfEditorStore((s) => s.currentPage);
    const goToPage = usePdfEditorStore((s) => s.goToPage);
    const selectObject = usePdfEditorStore((s) => s.selectObject);

    const digits = (n) => (isBn ? toBnDigits(n) : String(n));

    const pdf = usePdfEditorStore((s) => s.pdf);
    // Heading per page, read from the PDF's own text. Keyed by page uid so a
    // reordered or duplicated page keeps the heading it had.
    const [pageHeadings, setPageHeadings] = useState({});
    const readRef = useRef(new Set());

    useEffect(() => {
        if (!pdf) return undefined;
        let cancelled = false;

        const read = async () => {
            for (const page of pages) {
                if (cancelled) return;
                if (page.sourceIndex == null || readRef.current.has(page.uid)) continue;
                readRef.current.add(page.uid);
                let heading = null;
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const lines = await readTextLines(pdf, page.sourceIndex + 1);
                    // The biggest line is the heading — but only if it is
                    // bigger than the page's ordinary type. A page set in one
                    // size has no heading on it, and calling its first line one
                    // would fill the outline with body text.
                    const sizes = lines.map((l) => l.fontSize).filter(Boolean).sort((a, b) => a - b);
                    const body = sizes[Math.floor(sizes.length / 2)] || 0;
                    const biggest = lines
                        .filter((l) => l.text?.trim())
                        .sort((a, b) => (b.fontSize || 0) - (a.fontSize || 0))[0];
                    if (biggest && biggest.fontSize > body * 1.15) {
                        heading = biggest.text.trim().slice(0, 90);
                    }
                } catch {
                    /* a page whose text cannot be read simply has no heading */
                }
                if (cancelled) return;
                setPageHeadings((prev) => ({ ...prev, [page.uid]: heading }));
            }
        };

        read();
        return () => { cancelled = true; };
    }, [pdf, pages]);

    const rows = useMemo(() => pages.map((page, index) => {
        const objects = objectsByUid[page.uid] || [];
        const texts = objects
            .filter((obj) => obj.type === OBJ.TEXT && obj.text?.trim() && !obj.role)
            .sort((a, b) => (b.fontSize || 0) - (a.fontSize || 0));
        const added = texts[0];
        const own = pageHeadings[page.uid];
        // What the editor put on the page wins: it is the newer statement about
        // what this page now says.
        const heading = added
            ? { id: added.id, text: added.text.split('\n')[0].trim().slice(0, 90) }
            : (own ? { id: `own-${page.uid}`, text: own } : null);
        return {
            index,
            marks: bookmarks.filter((mark) => mark.pageIndex === index),
            heading,
            // Everything else on the page, as a count — enough to tell a page
            // with a paragraph on it from an empty one.
            others: Math.max(0, texts.length - 1),
        };
    }), [pages, objectsByUid, bookmarks, pageHeadings]);

    return (
        <div className="min-h-0 flex-1 overflow-y-auto bg-[#FBFAF9] px-4 py-4 [scrollbar-width:thin] dark:bg-zinc-950">
            <div className="mx-auto max-w-[760px]">
                {rows.map((row) => (
                    <div
                        key={row.index}
                        className={`mb-1.5 rounded-xl border px-3 py-2.5 transition-colors ${
                            row.index === currentPage
                                ? 'border-primary/50 bg-primary-50/60 dark:border-primary/40 dark:bg-primary-950/20'
                                : 'border-[#ECE8E4] bg-white hover:border-primary/30 dark:border-zinc-800 dark:bg-zinc-900'
                        }`}
                    >
                        <button
                            type="button"
                            onClick={() => goToPage(row.index)}
                            className="flex w-full items-center gap-2.5 text-left"
                        >
                            <span className="flex h-6 w-8 flex-none items-center justify-center rounded-md bg-[#F0ECE8] text-[11px] font-bold tabular-nums text-[#6B635C] dark:bg-zinc-800 dark:text-zinc-300">
                                {digits(row.index + 1)}
                            </span>
                            {row.heading ? (
                                <>
                                    <IconHeading size={14} stroke={1.9} className="flex-none text-[#A8A099]" />
                                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                        {row.heading.text}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <IconFileText size={14} stroke={1.9} className="flex-none text-[#C3BEB4]" />
                                    <span className="min-w-0 flex-1 truncate text-[13px] text-[#A8A099]">
                                        {t('pdfEditor.outline.noHeading')}
                                    </span>
                                </>
                            )}
                            {row.others ? (
                                <span className="flex-none text-[11px] text-[#A8A099]">
                                    {t('pdfEditor.outline.blocks', { count: digits(row.others) })}
                                </span>
                            ) : null}
                        </button>

                        {/* Bookmarks on this page sit under it, indented — they
                            are the structure someone actually declared. */}
                        {row.marks.map((mark) => (
                            <button
                                key={mark.id}
                                type="button"
                                onClick={() => { goToPage(mark.pageIndex); selectObject(null); }}
                                className="mt-1 flex w-full items-center gap-2 pl-10 text-left"
                                style={{ paddingLeft: 40 + (mark.level || 0) * 16 }}
                            >
                                <IconBookmark size={12} stroke={1.9} className="flex-none text-primary" />
                                <span className="min-w-0 flex-1 truncate text-[12px] text-[#3D3833] dark:text-zinc-300">
                                    {mark.title}
                                </span>
                            </button>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}
